import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { SectionView, Button, Skeleton, REPORT_PART_LABELS } from '@rb/ui'
import type { AnalysisResults, ProjectDetailData, ReportPart, Section } from '@rb/ui'
import type { ProjectStatus } from '@rb/ui'
import { useFetch } from '../hooks/useFetch'
import { SCHAFFERHOLZ_REPORT } from '../data/schafferholzReport'
import styles from './ProjectPart.module.css'

interface ProjectResponse {
  project: ProjectDetailData
  analysis_results: AnalysisResults | null
}

const VALID_PARTS: ReportPart[] = ['analyse', 'vorschlag', 'roi', 'finanzierung']

function isReportPart(value: string | undefined): value is ReportPart {
  return !!value && (VALID_PARTS as string[]).includes(value)
}

// Scroll-spy active band: sections are observed against the viewport (the
// AppShell content area scrolls within it). This top offset places the "active"
// trigger line just below the app header so the highlighted item matches what's
// at the top of the visible content.
const SCROLL_OFFSET = 72

export default function ProjectPart() {
  const { id, part } = useParams<{ id: string; part: string }>()
  const navigate = useNavigate()

  const { data, loading, error } = useFetch<ProjectResponse>(`/projects/${id}`)
  const contentRef = useRef<HTMLDivElement>(null)
  const [activeId, setActiveId] = useState('')

  const project = data?.project
  const status = (project?.status ?? 'uploaded') as ProjectStatus
  const hasResults = status === 'results_available'
  const validPart = isReportPart(part) ? part : null

  const PART_COLUMN: Record<ReportPart, keyof Pick<AnalysisResults, 'analysis' | 'proposal' | 'roi' | 'financing'>> = {
    analyse: 'analysis',
    vorschlag: 'proposal',
    roi: 'roi',
    finanzierung: 'financing',
  }

  let sections: Section[] = []
  if (hasResults && validPart) {
    const ar = data?.analysis_results
    if (ar) {
      const col = ar[PART_COLUMN[validPart]]
      sections = Array.isArray(col) ? col : []
    } else if (import.meta.env.DEV) {
      sections = SCHAFFERHOLZ_REPORT[validPart]
    }
  }
  const sectionCount = sections.length

  // Scroll-spy: highlight the nav item for the section currently near the top.
  useEffect(() => {
    const root = contentRef.current
    if (!root) return
    const els = Array.from(root.querySelectorAll<HTMLElement>('[data-section-id]'))
    if (!els.length) return
    setActiveId((cur) => cur || els[0].dataset.sectionId || '')

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        const top = visible[0]?.target as HTMLElement | undefined
        if (top?.dataset.sectionId) setActiveId(top.dataset.sectionId)
      },
      { rootMargin: `-${SCROLL_OFFSET}px 0px -55% 0px`, threshold: 0 }
    )
    els.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [sectionCount])

  function scrollToSection(sectionId: string) {
    const el = contentRef.current?.querySelector<HTMLElement>(`[data-section-id="${sectionId}"]`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setActiveId(sectionId)
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <Skeleton className={styles.skeletonTitle} />
        <Skeleton className={styles.skeletonBody} />
      </div>
    )
  }

  if (error || !project || !validPart) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState}>
          <p>Dieser Bereich ist nicht verfügbar.</p>
          <Button variant="secondary" onClick={() => navigate(`/projects/${id}`)}>
            Zurück zum Projekt
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <button className={styles.backLink} onClick={() => navigate(`/projects/${project.id}`)}>
        ← {project.name || '(Ohne Titel)'}
      </button>

      <h1 className={styles.heading}>{REPORT_PART_LABELS[validPart]}</h1>

      {sectionCount === 0 ? (
        <div className={styles.emptyState}>Für diesen Bereich liegen noch keine Inhalte vor.</div>
      ) : (
        <div className={styles.layout}>
          <nav className={styles.nav} aria-label="Abschnitte">
            <ul className={styles.navList}>
              {sections.map((section, i) => {
                const sid = `sec-${i}`
                return (
                  <li key={sid}>
                    <button
                      className={[styles.navItem, activeId === sid ? styles.navItemActive : '']
                        .filter(Boolean)
                        .join(' ')}
                      aria-current={activeId === sid ? 'true' : undefined}
                      onClick={() => scrollToSection(sid)}
                    >
                      {section.section_title}
                    </button>
                  </li>
                )
              })}
            </ul>
          </nav>

          <div className={styles.content} ref={contentRef}>
            {sections.map((section, i) => {
              const sid = `sec-${i}`
              return (
                <section key={sid} data-section-id={sid} className={styles.sectionBlock}>
                  <h2 className={styles.sectionTitle}>{section.section_title}</h2>
                  <SectionView section={section} />
                </section>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
